import React from 'react'
import { Route } from 'react-router'
import styled from 'styled-components'

import { MountHandle } from '@sketch/utils'
import SettingsForm from './SettingsForm'
import { EmailNotificationsPanel } from '@sketch/user'
import { Section } from '@sketch/components'
import { OtherOptionsPanel } from './OtherOptionsPanel'
import { SignInMethods } from './SignInMethods'
import ManageScimPersonalAccessTokens from '../ManageScimPersonalAccessTokens'

import {
  routes,
  isSsoAuthorization,
  useUserAuthorizations,
  useUserProfile,
  useIsSocialSignInOnly,
  getPersonalAuthorization,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'

import { isPrivateCloud } from '@sketch/env-config'

import {
  useGetUserSettingsQuery,
  useUserUpdateMutation,
  InitialUserFragment,
  UserWorkspaceMembershipFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

const isPasswordOnlyAccount = (
  signInOptions: InitialUserFragment['signInOptions']
): boolean => {
  return !!(signInOptions.length === 1 && signInOptions[0] === 'PASSWORD')
}

const createOptimisticResponse = (
  user: InitialUserFragment,
  receiveEmailNotifications: boolean
) => ({
  __typename: 'RootMutationType' as const,
  userUpdate: {
    __typename: 'UserUpdatePayload' as const,
    errors: [],
    successful: true,
    user: {
      ...user,
      receiveEmailNotifications,
    },
  },
})

const RelativeContainer = styled.div`
  position: relative;
  margin-bottom: 64px;
`

const GeneralSettings: React.FC<{
  currentWorkspace?: WorkspaceMinimalFragment
}> = ({ currentWorkspace }) => {
  const { showToast } = useToast()

  const isSocialSignInOnly = useIsSocialSignInOnly()

  const { data: userData } = useUserProfile()
  const { authorizations, hasPersonalAuthorization } = useUserAuthorizations()

  const [updateUser, { called }] = useUserUpdateMutation({
    redirectErrors: false,
    onCompleted: data => {
      const { errors, successful } = data.userUpdate || {}
      if (successful) {
        showToast('Profile settings updated')
      }

      if (errors?.length! > 0) {
        const errorMessage = errors!.map(error => error?.message).join('; ')
        showToast(errorMessage, 'negative')
      }
    },
    onError: error => {
      showToast(error.message, 'negative')
    },
  })

  const { data: userSettingsData } = useGetUserSettingsQuery()

  // Merge the user objects, more precisely get the "newEmail".
  const user = {
    ...userData!.me!,
    ...userSettingsData?.me!,
  }

  const isPasswordOnly = isPasswordOnlyAccount(user.signInOptions)
  const isSsoOnly = !getPersonalAuthorization()

  const memberships: UserWorkspaceMembershipFragment[] =
    userSettingsData?.me?.workspaceMemberships?.entries || []

  const isSignedInWithSso = authorizations.some(isSsoAuthorization)

  // Show the subscribe to email comments IF the user has personal identity OR it's a business workspace
  const showEmailNotificationsPanel =
    user.hasPersonalIdentity || currentWorkspace?.type === 'BUSINESS'

  return (
    <>
      <Route
        path={routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template()}
        render={({ history }) => (
          <MountHandle
            onMount={async () => {
              // Prevent calling the mutation again if it's already called (when mounting)
              // Related to https://github.com/sketch-hq/Cloud/issues/6102#issuecomment-804823199
              if (called) {
                return
              }

              user.hasPersonalIdentity &&
                (await updateUser({
                  variables: { input: { receiveEmailNotifications: false } },
                  optimisticResponse: createOptimisticResponse(user, false),
                }))

              history.replace(routes.PERSONAL_SETTINGS.create({}))
            }}
          />
        )}
      />
      <RelativeContainer>
        <Section title="Your Account Settings" align="center">
          <SettingsForm
            userName={user.name!}
            userEmail={user.email!}
            userNewEmail={user.newEmail}
            userAvatarSrc={user.avatar?.large || user.avatar!.small!}
            userHasAvatar={user.hasAvatar!}
            userHasPersonalIdentity={user.hasPersonalIdentity}
            userHasPersonalAuthorization={hasPersonalAuthorization}
            isSignedInWithSso={isSignedInWithSso}
            onUserUpdate={updateUser}
            isSocialSignInOnly={isSocialSignInOnly}
            isPasswordOnlyAccount={isPasswordOnly}
          />
        </Section>
      </RelativeContainer>

      {!isSsoOnly && <SignInMethods />}

      {showEmailNotificationsPanel && (
        <EmailNotificationsPanel
          receiveEmailNotifications={user.receiveEmailNotifications!}
        />
      )}

      {isPrivateCloud && user.isPaasAdmin && <ManageScimPersonalAccessTokens />}

      <OtherOptionsPanel
        hasPersonalIdentity={user.hasPersonalIdentity}
        memberships={memberships}
        isSocialSignInOnly={isSocialSignInOnly}
      />
    </>
  )
}

export default GeneralSettings
